import { useEffect, useMemo, useState } from 'react';
import { useAppointments } from '@/hooks/useAppointments';
import { useSettingsStore, useAuthStore } from '@/stores';
import { AppointmentCard } from '@/components/cards/AppointmentCard';
import { SearchBar } from '@/components/filters/SearchBar';
import { StatusChips } from '@/components/filters/StatusChips';
import { StaffFilter } from '@/components/filters/StaffFilter';
import { LocationFilter } from '@/components/filters/LocationFilter';
import { SortMenu } from '@/components/filters/SortMenu';
import { GroupingMenu } from '@/components/filters/GroupingMenu';
import { DateNavigator } from '@/components/common/DateNavigator';
import { CardSkeleton } from '@/components/common/SkeletonLoader';
import { getApplicableRate, fetchHistoricalRates, loadRatesCache } from '@/services/rates.service';
import { resolveLocationName } from '@/utils/locationResolution';
import { Calendar, ChevronDown, ChevronRight } from 'lucide-react';
import type { Appointment, AppointmentStatus, GroupBy, SortField } from '@/types/appointments';

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos' },
  { value: 'unconfirmed', label: 'No confirmadas' },
  { value: 'confirmed', label: 'Confirmadas' },
  { value: 'arrived', label: 'Llegaron' },
  { value: 'started', label: 'En curso' },
  { value: 'completed', label: 'Completadas' },
  { value: 'no-show', label: 'No vinieron' },
  { value: 'cancelled', label: 'Canceladas' },
];

const SORT_OPTIONS = [
  { value: 'time', label: 'Hora' },
  { value: 'name', label: 'Nombre' },
  { value: 'status', label: 'Estado' },
];

const GROUP_OPTIONS = [
  { value: 'none', label: 'Sin agrupar' },
  { value: 'time', label: 'Por hora' },
  { value: 'employee', label: 'Por profesional' },
  { value: 'status', label: 'Por estado' },
];

export function AppointmentsPage() {
  const settings = useSettingsStore();
  const { locations, employees } = useAuthStore();
  const {
    items, rawItems, groupedItems, loading, selectedDate,
    searchQuery, filterStatus, filterEmployee, filterLocation,
    sortBy, sortOrder, groupBy,
    setSelectedDate, setSearchQuery, setFilterStatus,
    setFilterEmployee, setFilterLocation,
    setSortBy, setSortOrder, setGroupBy, refresh,
    markProcessed, unmarkProcessed, isProcessed,
  } = useAppointments();

  const [ratesMap, setRatesMap] = useState<Record<string, { rate: number; date: string; source: string }>>({});
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  // Build reactive staff options with appointment counts
  const staffOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const emp of employees) {
      if (emp?.id) {
        const name =
          emp.attributes?.name ||
          `${emp.attributes?.['first-name'] || ''} ${emp.attributes?.['last-name'] || ''}`.trim() ||
          `Staff #${emp.id}`;
        map.set(String(emp.id), { id: String(emp.id), name });
      }
    }
    for (const apt of rawItems) {
      if (apt.employeeId && !map.has(String(apt.employeeId))) {
        map.set(String(apt.employeeId), {
          id: String(apt.employeeId),
          name: apt.employeeName || `Staff #${apt.employeeId}`,
        });
      }
    }
    return Array.from(map.values()).map((emp) => ({
      ...emp,
      count: rawItems.filter(
        (a) => a.employeeId === emp.id || a.employeeName === emp.name
      ).length,
    }));
  }, [employees, rawItems]);

  // Build reactive location options with appointment counts
  const locationOptions = useMemo(() => {
    const map = new Map<string, { id: string; name: string }>();
    for (const loc of locations) {
      if (loc?.id) {
        const name = resolveLocationName(
          loc.id,
          loc.attributes?.name,
          locations,
          settings.locationAliases,
          settings.locationNameMode
        );
        map.set(String(loc.id), { id: String(loc.id), name });
      }
    }
    for (const apt of rawItems) {
      if (apt.locationId && !map.has(String(apt.locationId))) {
        const name = resolveLocationName(
          apt.locationId,
          apt.locationName,
          locations,
          settings.locationAliases,
          settings.locationNameMode
        );
        map.set(String(apt.locationId), {
          id: String(apt.locationId),
          name: name || `Sede #${apt.locationId}`,
        });
      }
    }
    return Array.from(map.values()).map((loc) => ({
      ...loc,
      count: rawItems.filter((a) => String(a.locationId) === loc.id).length,
    }));
  }, [locations, rawItems, settings.locationAliases, settings.locationNameMode]);

  // Preload historical rates and resolve applicable rate per appointment reactively
  useEffect(() => {
    let isMounted = true;

    if (settings.currencyDisplayMode === 'manual') {
      return;
    }

    const code: 'USD' | 'EUR' = settings.currencyCode === 'EUR' ? 'EUR' : 'USD';
    const fillMode = settings.historicalRateFillMode || 'next';

    fetchHistoricalRates(code)
      .then(() => {
        if (!isMounted) return;

        const promises = items.map(async (apt) => {
          const isPendingDebt = apt.salePaymentStatus === 'UNPAID' || apt.status === 'unconfirmed';
          const record = await getApplicableRate(apt.date, isPendingDebt, code, fillMode);
          return { id: apt.id, record };
        });

        Promise.all(promises)
          .then((results) => {
            if (!isMounted) return;
            const nextMap: Record<string, { rate: number; date: string; source: string }> = {};
            for (const res of results) {
              nextMap[res.id] = {
                rate: res.record.rate,
                date: res.record.date,
                source: res.record.source,
              };
            }
            setRatesMap(nextMap);
          })
          .catch(() => {});
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [items, settings.currencyDisplayMode, settings.currencyCode, settings.historicalRateFillMode]);

  const resolveRateForAppointment = (apt: Appointment) => {
    if (settings.currencyDisplayMode === 'manual') {
      return {
        rate: settings.manualRate || 0,
        date: '',
        source: 'manual',
      };
    }

    if (ratesMap[apt.id]) {
      return ratesMap[apt.id];
    }

    const code: 'USD' | 'EUR' = settings.currencyCode === 'EUR' ? 'EUR' : 'USD';
    const cached = loadRatesCache().latest[code];
    return {
      rate: cached?.rate || 0,
      date: cached?.date || apt.date || '',
      source: cached?.source || 'historical',
    };
  };

  // Count items per status respecting active staff/location filter
  const statusCounts = STATUS_OPTIONS.map((opt) => {
    const baseList = rawItems.filter((a) => {
      if (filterEmployee !== 'all' && a.employeeId !== filterEmployee && a.employeeName !== filterEmployee) {
        return false;
      }
      if (filterLocation !== 'all' && String(a.locationId) !== String(filterLocation)) {
        return false;
      }
      return true;
    });
    return {
      ...opt,
      count: opt.value === 'all'
        ? baseList.length
        : baseList.filter((a) => a.status === opt.value).length,
    };
  });

  return (
    <div className="space-y-4 py-4">
      {/* Date navigation */}
      <DateNavigator
        selectedDate={selectedDate}
        onDateChange={setSelectedDate}
        onRefresh={refresh}
        loading={loading}
      />

      {/* Filters */}
      <SearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Buscar cliente, servicio..."
      />
      <StatusChips
        options={statusCounts}
        selected={filterStatus || 'all'}
        onChange={(val) => setFilterStatus(val as AppointmentStatus | 'all')}
      />

      {/* Toolbar: Staff, Location, Counts, Grouping & Sort */}
      <div className="flex flex-wrap items-center justify-between gap-2.5 pt-0.5">
        <div className="flex flex-wrap items-center gap-2">
          {staffOptions.length > 0 && (
            <StaffFilter
              options={staffOptions}
              selected={filterEmployee}
              onChange={setFilterEmployee}
            />
          )}
          {locationOptions.length > 1 && (
            <LocationFilter
              options={locationOptions}
              selected={filterLocation}
              onChange={setFilterLocation}
            />
          )}
        </div>

        <div className="flex items-center gap-2.5 ml-auto">
          <span className="text-xs text-text-muted whitespace-nowrap">
            {items.length} cita{items.length !== 1 ? 's' : ''}
          </span>
          <GroupingMenu
            options={GROUP_OPTIONS}
            selected={groupBy}
            onChange={(val) => setGroupBy(val as GroupBy)}
          />
          <SortMenu
            options={SORT_OPTIONS}
            selected={sortBy}
            order={sortOrder}
            onSort={(field) => setSortBy(field as SortField)}
            onToggleOrder={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-3">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-text-muted">
          <Calendar size={48} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No hay citas para esta fecha</p>
        </div>
      ) : groupBy === 'none' ? (
        <div className="space-y-3">
          {items.map((apt) => {
            const rateInfo = resolveRateForAppointment(apt);
            return (
              <AppointmentCard
                key={apt.id}
                appointment={apt}
                isProcessed={isProcessed(apt.id)}
                onMarkProcessed={() =>
                  isProcessed(apt.id) ? unmarkProcessed(apt.id) : markProcessed(apt.id)
                }
                rate={rateInfo.rate}
                rateDate={rateInfo.date}
                rateSource={rateInfo.source}
              />
            );
          })}
        </div>
      ) : (
        <div className="space-y-3">
          {Object.entries(groupedItems).map(([group, groupItems]) => {
            const isCollapsed = !!collapsedGroups[group];
            return (
              <div key={group} className="space-y-2">
                {group && (
                  <button
                    type="button"
                    onClick={() => toggleGroup(group)}
                    className="w-full flex items-center justify-between py-2 px-3 bg-surface-secondary/90 hover:bg-surface-secondary rounded-xl border border-border mt-2 shadow-2xs cursor-pointer transition-colors text-left"
                  >
                    <div className="flex items-center gap-2">
                      {isCollapsed ? (
                        <ChevronRight size={15} className="text-text-muted transition-transform" />
                      ) : (
                        <ChevronDown size={15} className="text-text-muted transition-transform" />
                      )}
                      <span className="text-xs font-semibold text-text-primary">{group}</span>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-surface-tertiary text-text-muted">
                      {groupItems.length}
                    </span>
                  </button>
                )}
                {!isCollapsed && (
                  <div className="space-y-3 pt-1">
                    {groupItems.map((apt) => {
                      const rateInfo = resolveRateForAppointment(apt);
                      return (
                        <AppointmentCard
                          key={apt.id}
                          appointment={apt}
                          isProcessed={isProcessed(apt.id)}
                          onMarkProcessed={() =>
                            isProcessed(apt.id) ? unmarkProcessed(apt.id) : markProcessed(apt.id)
                          }
                          rate={rateInfo.rate}
                          rateDate={rateInfo.date}
                          rateSource={rateInfo.source}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
